import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { AlertCircle } from "lucide-react";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

export default function Login() {
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState("");
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const hasCheckedRef = useRef(false);

  const checkAuth = useCallback(async () => {
    // Prevent duplicate checks
    if (hasCheckedRef.current) {
      return;
    }
    hasCheckedRef.current = true;
    
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true  // CRITICAL: Ensure cookies are sent
      });
      
      if (response.data && response.data.email) {
        localStorage.setItem("user", JSON.stringify(response.data));
        navigate("/", { replace: true });
      }
    } catch (error) {
      // Not authenticated, stay on login page - this is expected
      localStorage.removeItem("user");
      localStorage.removeItem("session_token");
      delete axios.defaults.headers.common["Authorization"];
    } finally {
      setIsCheckingAuth(false);
    }
  }, [navigate]);

  // Check for auth errors and if already authenticated
  useEffect(() => {
    // Check for stored error message from AuthCallback
    const storedError = sessionStorage.getItem("auth_error");
    if (storedError) {
      setErrorMessage(storedError);
      sessionStorage.removeItem("auth_error");
    }
    checkAuth();
  }, [checkAuth]);

  const handleGoogleSignIn = () => {
    // Clear any previous error
    setErrorMessage("");
    sessionStorage.removeItem("auth_error");
    sessionStorage.removeItem("processed_session_id");
    
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    console.log("[Login] Initiating Google Sign-In, redirect URL:", redirectUrl);
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Show loading while checking auth
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-white p-0 sm:p-4">
    <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen sm:min-h-[calc(100vh-2rem)] w-full bg-white sm:rounded-[1.75rem] sm:border sm:border-slate-100 sm:shadow-[0_30px_90px_-40px_rgba(13,148,136,0.25)] overflow-hidden">
      {/* LEFT — light white→mint analytics visual */}
      <div className="relative hidden lg:flex flex-col justify-center overflow-hidden px-14 py-12"
           style={{ background: "linear-gradient(150deg, #f2fdfb 0%, #eafcf7 45%, #e6faf3 100%)" }}>
        {/* abstract teal background shapes */}
        <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
          {/* large teal quarter circle top-right */}
          <div className="absolute -top-40 -right-40 h-[34rem] w-[34rem] rounded-full"
               style={{ background: "radial-gradient(circle at 35% 65%, #0f9d8f 0%, #17a897 45%, rgba(45,212,191,0.35) 78%, transparent 100%)", opacity: 0.9 }} />
          <div className="absolute top-10 right-24 h-40 w-40 rounded-full opacity-30"
               style={{ background: "radial-gradient(circle, rgba(153,246,228,0.9) 0%, transparent 70%)" }} />
          <div className="absolute -bottom-24 -left-20 h-80 w-80 rounded-full opacity-40"
               style={{ background: "radial-gradient(circle, rgba(45,212,191,0.18) 0%, transparent 70%)" }} />
        </div>

        {/* Top brand label */}
        <div className="relative z-10 flex items-center gap-3 mb-7">
          <span className="text-xs font-semibold tracking-[0.35em] text-slate-500">OMNIX</span>
          <span className="h-px w-10 bg-slate-400/50" />
        </div>

        {/* Heading */}
        <div className="relative z-10 max-w-xl">
          <h1 className="text-5xl xl:text-6xl font-bold leading-[1.03] tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <span className="text-slate-800">From Quotations</span><br />
            <span className="text-slate-800">to </span>
            <span style={{ color: '#0d9488' }}>Growth.</span>
          </h1>
          <p className="mt-4 text-lg text-slate-500">Smarter quotations. Better decisions.</p>
        </div>

        {/* Illustrative dashboard composition (static UI only — no data fetched) */}
        <div className="relative z-10 mt-16 mb-10 max-w-2xl" aria-hidden="true">
          {/* Tablet / dashboard mockup */}
          <div className="rounded-[1.25rem] bg-white border border-slate-200/70 shadow-[0_30px_80px_-30px_rgba(13,148,136,0.35)] p-3.5">
            <div className="flex gap-3.5">
              {/* Mini sidebar */}
              <div className="w-36 shrink-0 rounded-xl bg-slate-50/70 p-2.5">
                <div className="flex items-center gap-1.5 px-1.5 pb-3">
                  <span className="flex h-5 w-5 items-center justify-center rounded-full border-2 border-teal-600 text-teal-600 text-[9px] font-bold">O</span>
                  <span className="text-[11px] font-bold tracking-[0.15em] text-slate-700">OMNIX</span>
                </div>
                {[
                  { label: "Dashboard", active: true },
                  { label: "Products" },
                  { label: "Sellers" },
                  { label: "Quotations" },
                  { label: "Discount Approvals" },
                  { label: "Inventory Allocation" },
                  { label: "Analytics" },
                ].map((it) => (
                  <div key={it.label}
                       className={`flex items-center gap-1.5 rounded-md px-1.5 py-1 mb-0.5 ${it.active ? "bg-teal-600" : ""}`}>
                    <span className={`h-2.5 w-2.5 rounded-[3px] ${it.active ? "bg-white/90" : "bg-slate-300"}`} />
                    <span className={`text-[9.5px] font-medium truncate ${it.active ? "text-white" : "text-slate-500"}`}>{it.label}</span>
                  </div>
                ))}
              </div>

              {/* Main content */}
              <div className="flex-1 min-w-0">
                {/* Revenue Trend */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] font-semibold text-slate-700">Revenue Trend</span>
                  <span className="text-[8.5px] font-medium text-teal-600 bg-teal-50 rounded-full px-2 py-0.5">This Quarter ▾</span>
                </div>
                <div className="rounded-lg border border-slate-100 p-2 mb-3">
                  <svg viewBox="0 0 300 96" className="w-full h-20" fill="none" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="rev" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#2dd4bf" stopOpacity="0.28" />
                        <stop offset="100%" stopColor="#2dd4bf" stopOpacity="0" />
                      </linearGradient>
                    </defs>
                    {[20,44,68].map((y)=>(<line key={y} x1="0" y1={y} x2="300" y2={y} stroke="#f1f5f9" strokeWidth="1" />))}
                    <path d="M6 74 L54 60 L102 66 L150 42 L198 48 L246 26 L294 14 L294 88 L6 88 Z" fill="url(#rev)" />
                    <path d="M6 74 L54 60 L102 66 L150 42 L198 48 L246 26 L294 14" stroke="#0d9488" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                    {[[6,74],[54,60],[102,66],[150,42],[198,48],[246,26],[294,14]].map(([cx,cy],i)=>(<circle key={i} cx={cx} cy={cy} r="3" fill="#fff" stroke="#0d9488" strokeWidth="1.8" />))}
                  </svg>
                  <div className="mt-1 flex justify-between text-[8px] text-slate-400 px-1">
                    {["Jan","Feb","Mar","Apr","May","Jun"].map((m)=>(<span key={m}>{m}</span>))}
                  </div>
                </div>

                {/* Brand Contribution */}
                <div className="rounded-lg border border-slate-100 p-2.5">
                  <span className="text-[11px] font-semibold text-slate-700">Brand Contribution</span>
                  <div className="mt-2 flex items-center gap-4">
                    <svg viewBox="0 0 42 42" className="w-[74px] h-[74px] shrink-0">
                      <circle cx="21" cy="21" r="15.9" fill="none" stroke="#f1f5f9" strokeWidth="6" />
                      <circle cx="21" cy="21" r="15.9" fill="none" stroke="#0d9488" strokeWidth="6" strokeDasharray="52 48" strokeDashoffset="25" />
                      <circle cx="21" cy="21" r="15.9" fill="none" stroke="#14b8a6" strokeWidth="6" strokeDasharray="24 76" strokeDashoffset="-27" />
                      <circle cx="21" cy="21" r="15.9" fill="none" stroke="#5eead4" strokeWidth="6" strokeDasharray="14 86" strokeDashoffset="-51" />
                      <circle cx="21" cy="21" r="15.9" fill="none" stroke="#a7f3e0" strokeWidth="6" strokeDasharray="10 90" strokeDashoffset="-65" />
                    </svg>
                    <div className="flex-1 space-y-1">
                      {[
                        { name: "Wellcore", pct: "52%", c: "#0d9488" },
                        { name: "Dynamite", pct: "24%", c: "#14b8a6" },
                        { name: "Youwefit", pct: "14%", c: "#5eead4" },
                        { name: "Others", pct: "10%", c: "#a7f3e0" },
                      ].map((b)=>(
                        <div key={b.name} className="flex items-center justify-between text-[9.5px]">
                          <span className="flex items-center gap-1.5 text-slate-600">
                            <span className="h-2 w-2 rounded-full" style={{ background: b.c }} />{b.name}
                          </span>
                          <span className="font-semibold text-slate-700">{b.pct}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Floating KPI cards */}
          <div className="absolute -top-9 -left-6 rounded-xl bg-white border border-slate-200/70 shadow-[0_16px_40px_-16px_rgba(13,148,136,0.4)] px-4 py-3 w-44">
            <div className="flex items-center gap-1.5 mb-1">
              <svg className="w-3.5 h-3.5 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M6 2h9l5 5v13a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1Z"/><path d="M15 2v5h5"/></svg>
              <span className="text-[10px] text-slate-400">Total Quotations</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-slate-800">248</span>
              <span className="flex items-center gap-0.5 text-xs font-semibold text-emerald-600">
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M7 14l5-5 5 5" strokeLinecap="round" strokeLinejoin="round"/></svg>+18%
              </span>
            </div>
          </div>

          <div className="absolute -bottom-9 -left-8 rounded-xl bg-white border border-slate-200/70 shadow-[0_16px_40px_-16px_rgba(13,148,136,0.4)] px-4 py-3 w-44">
            <div className="flex items-center gap-1.5 mb-1">
              <svg className="w-3.5 h-3.5 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span className="text-[10px] text-slate-400">Pending Approvals</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-slate-800">12</span>
              <span className="flex items-center gap-0.5 text-xs font-semibold text-orange-500">
                <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><path d="M7 10l5 5 5-5" strokeLinecap="round" strokeLinejoin="round"/></svg>-25%
              </span>
            </div>
          </div>

          <div className="absolute top-1/2 -right-6 -translate-y-1/2 rounded-xl bg-white border border-slate-200/70 shadow-[0_16px_40px_-16px_rgba(13,148,136,0.4)] px-4 py-3 w-40">
            <div className="flex items-center gap-1.5 mb-2">
              <span className="flex h-5 w-5 items-center justify-center rounded-md bg-teal-50 text-teal-600 text-xs font-bold">%</span>
              <span className="text-[10px] text-slate-500">Discount Validation</span>
            </div>
            <div className="inline-flex items-center gap-1.5 rounded-full bg-teal-50 px-2.5 py-1 text-teal-700">
              <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/></svg>
              <span className="text-xs font-semibold">Within Limit</span>
            </div>
          </div>

          {/* Handwritten accent */}
          <div className="absolute -bottom-16 right-2 flex items-end gap-1">
            <span className="text-teal-600 text-2xl leading-tight" style={{ fontFamily: '"Segoe Script","Brush Script MT","Comic Sans MS",cursive', transform: "rotate(-8deg)" }}>
              Deals<br />Dispatched<br />Growth
            </span>
            <svg className="w-8 h-12 text-teal-500 mb-1" viewBox="0 0 40 60" fill="none" stroke="currentColor" strokeWidth="2.2">
              <path d="M6 54 C 2 30 20 16 34 12" strokeLinecap="round" />
              <path d="M26 10 L35 11 L31 20" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 mt-4">
          <p className="text-[11px] font-medium tracking-[0.15em] text-slate-400">
            ONE PLATFORM&nbsp;&nbsp;|&nbsp;&nbsp;SMARTER DISCOUNTS&nbsp;&nbsp;|&nbsp;&nbsp;STRONGER PARTNERSHIPS
          </p>
        </div>
      </div>

      {/* RIGHT — minimal, centered Google sign-in */}
      <div className="flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md space-y-7 text-center">
          <div className="space-y-5">
            <img
              src="/omnix-logo-transparent.png"
              alt="OMNIX by wellversed"
              className="h-36 w-auto mx-auto object-contain"
              data-testid="login-brand-logo"
            />
            <div className="space-y-2">
              <h1 className="text-3xl font-bold tracking-tight font-heading text-slate-800" data-testid="login-title">
                Welcome to Omnix
              </h1>
              <p className="text-slate-500">
                Sign in with your @wellversed.in Google account to continue
              </p>
            </div>
          </div>

          {errorMessage && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 text-left" data-testid="auth-error-message">
              <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">{errorMessage}</p>
                <p className="text-xs text-red-600 mt-1">Please use your @wellversed.in email address.</p>
              </div>
            </div>
          )}

          <button
            onClick={handleGoogleSignIn}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-slate-300 rounded-lg bg-white hover:bg-slate-50 transition-colors shadow-sm"
            data-testid="google-signin-button"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            <span className="text-sm font-medium text-slate-700">Sign in with Google</span>
          </button>

          <p className="text-center text-xs text-slate-400">
            By signing in, you agree to our terms of service and privacy policy
          </p>
        </div>
      </div>
    </div>
    </div>
  );
}
